// © Microsoft Corporation. All rights reserved.

import { OneServiceMetadataType } from "@msnews/river-data-types";
import { TelemetryBehaviorType, TelemetryContentType, TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { PublisherContentData } from "./PublisherCarouselData";
import { addOrUpdateTelemetryObject, decorateTelemetryHybridWC, SocialTelemetryManager } from "@msnews/social-utils";
import { TelemetryObject } from "@msnews/telemetry-core";

let hybridEnabled = false;

export interface PublisherCarouselTelemetryData {
    contentDataArray: PublisherContentData[];
}

export interface PublisherCarouselTelemetryContext {
    root: TelemetryObjectBase,
    carousel: TelemetryObjectBase,
    seeAllLink: TelemetryObjectBase,
    nextSlideArrow: TelemetryObjectBase
    previousSlideArrow: TelemetryObjectBase,
}

const publisherCarouselTelemetryContracts = {
    carousel: {
        name: "SubscriptionsUpsell"
    },
    seeAllLink: {
        name: "ViewProfile",
        content: {
            headline: "ViewProfile"
        }
    },
    nextSlideArrow: {
        name: "NextSlideArrow",
        behavior: TelemetryBehaviorType.More,
        content: {
            headline: "NextSlideArrow"
        }
    },
    previousSlideArrow: {
        name: "PreviousSlideArrow",
        behavior: TelemetryBehaviorType.More,
        content: {
            headline: "PreviousSlideArrow"
        }
    }
};

export const mapPublisherCarouselTelemetry = (
    rootTelemetryObject: TelemetryObjectBase,
    isHybrid?: boolean
): PublisherCarouselTelemetryContext => {
    hybridEnabled = isHybrid;
    if (isHybrid && !rootTelemetryObject) {
        return;
    }

    const carousel = newTelemetryObjectByContract(
        publisherCarouselTelemetryContracts.carousel,
        isHybrid,
        rootTelemetryObject
    );

    const seeAllLink = newTelemetryObjectByContract(
        publisherCarouselTelemetryContracts.seeAllLink,
        isHybrid,
        carousel
    );

    const nextSlideArrow = newTelemetryObjectByContract(
        publisherCarouselTelemetryContracts.nextSlideArrow,
        isHybrid,
        carousel
    );

    const previousSlideArrow = newTelemetryObjectByContract(
        publisherCarouselTelemetryContracts.previousSlideArrow,
        isHybrid,
        carousel
    );

    return {
        root: rootTelemetryObject,
        carousel,
        seeAllLink,
        nextSlideArrow,
        previousSlideArrow
    };
};

function newTelemetryObjectByContract(contract, isHybrid, parentTelemetryObject) {
    if (isHybrid) {
        return addOrUpdateTelemetryObject(parentTelemetryObject, contract, {});
    }

    return new TelemetryObject(contract);
}

export function mapContentTypeTelemetryContentType(type: OneServiceMetadataType): number {
    if (type === OneServiceMetadataType.Article) {
        return TelemetryContentType.Article;
    }
    if (type === OneServiceMetadataType.Video) {
        return TelemetryContentType.Video;
    }
    if (type === OneServiceMetadataType.Slideshow) {
        return TelemetryContentType.Gallery;
    }
}

export function setupHybridTelemetry({handleTelemetryCall, handlePartialPageImpressionCall, handleUpdateTemplValueCallback}): boolean {
    if (handleTelemetryCall && handlePartialPageImpressionCall) {
        SocialTelemetryManager.setPrimeHandlers({
            handleTelemetryCall,
            handlePartialPageImpressionCall,
            handleUpdateTemplValueCallback
        });
        return true;
    }
    return false;
}

export function renderHybridTelemetryAsync(
    telemetryObject: any,
    sendManualClick: boolean,
    options?: { anchorParent?: boolean, isNavigate?: boolean }
) {
    if (!telemetryObject || !hybridEnabled) { return; }

    const { anchorParent, isNavigate } = options || {};
    const elementId = telemetryObject?.elementId || `t-${Date.now()}`;

    setTimeout(() => {
        try {
            let element = document.querySelector("publisher-carousel")?.shadowRoot?.querySelector(`[ht-id="${elementId}"]`);
            if (anchorParent) { element = element?.shadowRoot?.querySelector("a") || element; }

            const setTrackingAttributesCallback = decorateTelemetryHybridWC(telemetryObject, { isNavigate });
            setTrackingAttributesCallback(element, sendManualClick);
        } catch (err) {
            // no-op
        }
    }, 1000);

    telemetryObject.elementId = elementId;
    return elementId;
}
